import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/store';

import { ADD_QUOTE_KEYS } from './addQuote.const';
import { IAddQuoteSliceInitialState, IConsigneeDetailsData, IShipperDetailsData, IStopDetailsData } from './addQuote.interface';

const initialState: IAddQuoteSliceInitialState = {
    isCreateLoading: false,
    data: {
        isModalOpen: false,
        currentStep: ADD_QUOTE_KEYS.SHIPPER_DETAILS,
        completedSteps: [],
        revalidateStep: null,
        addQuoteData: null,
    },
};

export const AddQuoteSlice = createSlice({
    name: 'AddQuote',
    initialState,
    reducers: {
        resetAddQuoteData: (state, { payload }: PayloadAction<{ clearAll?: boolean; openModal?: boolean } | undefined>) => {
            const clearAll = payload?.clearAll ?? false;
            const openModal = payload?.openModal ?? false;

            if (clearAll) return initialState;

            return {
                ...initialState,
                data: {
                    ...initialState.data,
                    isModalOpen: openModal,
                    addQuoteData: {
                        ...initialState.data.addQuoteData,
                        serviceType: state.data.addQuoteData?.serviceType ?? null,
                        divisionId: state.data.addQuoteData?.divisionId ?? null,
                        customerBillingAccountId: state.data.addQuoteData?.customerBillingAccountId ?? null,
                        urgency: state.data.addQuoteData?.urgency ?? null,
                        serviceLevel: state.data.addQuoteData?.serviceLevel ?? null,
                        // Task 6 fix: stopCount is a modal-level selection (preserved like serviceType/divisionId);
                        // stops is per-step data (intentionally wiped like shipperDetails/consigneeDetails).
                        // Previously both were dropped on soft reset, causing multi-stop selections to vanish
                        // before NewAddQuote mounted. Now preserve stopCount only.
                        stopCount: state.data.addQuoteData?.stopCount ?? 2,
                    },
                },
            };
        },
        setAddQuoteStep: (state, { payload }) => {
            state.data.currentStep = payload;
        },
        // Step key whose form must re-run validation the moment it mounts. Set when a submit on a
        // LATER step is blocked by data the user never touched on this visit (a duplicated quote's
        // pickup date that has since gone into the past), because bouncing them back to the owning
        // step would otherwise show a pristine form with no indication of what to fix. The step
        // reads this, triggers yup so the offending field turns red, and clears it back to null.
        setAddQuoteRevalidateStep: (state, { payload }) => {
            state.data.revalidateStep = payload;
        },
        setAddQuoteCompletedStep: (state, { payload }) => {
            if (Array.isArray(payload)) {
                state.data.completedSteps = payload;
            } else {
                if (!state.data.completedSteps) {
                    state.data.completedSteps = [];
                }

                if (!state.data.completedSteps.includes(payload)) {
                    state.data.completedSteps.push(payload);
                }
            }
        },
        setAddQuoteData: (state, { payload }) => {
            state.data.addQuoteData = {
                ...state.data.addQuoteData,
                ...payload.data,
            };
        },
        setIsCreateLoading: (state, { payload }) => {
            state.isCreateLoading = payload;
        },
    },
});

export const { setAddQuoteStep, setAddQuoteRevalidateStep, resetAddQuoteData, setAddQuoteCompletedStep, setAddQuoteData, setIsCreateLoading } = AddQuoteSlice.actions;

export const addQuoteCurrentStep = (state: RootState): any => state.AddQuote.data?.currentStep;

export const addQuoteCompletedSteps = (state: RootState): string[] => state.AddQuote.data?.completedSteps || [];

export const addQuoteRevalidateStep = (state: RootState): string | null => state.AddQuote.data?.revalidateStep ?? null;
export const test = (state: RootState): any => state.AddQuote.data?.addQuoteData;

export const addQuoteDivisionId = (state: RootState): number | null => state.AddQuote.data?.addQuoteData?.divisionId || null;

export const addQuoteCustomerBillingAccountId = (state: RootState): number | null => state.AddQuote.data?.addQuoteData?.customerBillingAccountId || null;

export const addQuoteServiceType = (state: RootState): string | null => state.AddQuote.data?.addQuoteData?.serviceType || null;

export const addQuoteServiceLevel = (state: RootState): string | null => state.AddQuote.data?.addQuoteData?.serviceLevel || null;

export const addQuoteShipperDetails = (state: RootState): IShipperDetailsData | null => state.AddQuote.data?.addQuoteData?.shipperDetails || null;

export const addQuoteConsigneeDetails = (state: RootState): IConsigneeDetailsData | null => state.AddQuote.data?.addQuoteData?.consigneeDetails || null;

export const addQuoteShipmentDetails = (state: RootState): any => state.AddQuote.data?.addQuoteData?.shipmentDetails || null;

export const addQuoteDimensionsDetails = (state: RootState): any => state.AddQuote.data?.addQuoteData?.dimensionsDetails || null;

// Multi-stop route — defaults to 2 (direct) when null/absent, matching the modal's default selection
export const addQuoteStopCount = (state: RootState): number => state.AddQuote.data?.addQuoteData?.stopCount ?? 2;

export const addQuoteStops = (state: RootState): Record<number, IStopDetailsData | null> => state.AddQuote.data?.addQuoteData?.stops ?? {};

export const addQuoteAllData = (state: RootState): IAddQuoteSliceInitialState['data']['addQuoteData'] => state.AddQuote.data?.addQuoteData || null;

export const isCreateLoading = (state: RootState): boolean => state.AddQuote.isCreateLoading;

export const addQuoteIsModalOpen = (state: RootState): boolean => state.AddQuote.data?.isModalOpen ?? false;

export default AddQuoteSlice.reducer;
